import { useState } from "react";
import { useForm } from "@inertiajs/react";
import {
  Box,
  Container,
  Heading,
  Input,
  Button,
  VStack,
  Text,
  Flex,
  Badge,
  Field,
  Select,
  createListCollection,
  Card,
  Stack,
  CloseButton,
} from "@chakra-ui/react";
import PlayerSearchInput from "@/components/PlayerSearchInput";
import PrefectureSelect from "@/components/Event/PrefectureSelect";
import { Player, Event } from "@/types";
import { toDateTimeLocalValue } from "@/lib/utils";
import { event_path } from "@/routes";

interface NewEditProps {
  event: Event & {
    participants: Player[];
    collaborators: Player[];
  };
}

export default function Edit({ event }: NewEditProps) {
  const { data, setData, put, processing, errors } = useForm({
    name: event.name || "",
    public_status: event.public_status || "published",
    start_at: toDateTimeLocalValue(event.start_at || ""),
    end_at: toDateTimeLocalValue(event.end_at || ""),
    prefecture: event.prefecture || "",
    participants: event.participants?.map(p => p.player_id) || [],
    collaborators: event.collaborators?.map(p => p.player_id) || [],
  });

  const [selectedParticipants, setSelectedParticipants] = useState<Player[]>(event.participants || []);
  const [selectedCollaborators, setSelectedCollaborators] = useState<Player[]>(event.collaborators || []);

  // 参加者の追加処理
  const handleParticipantAdd = (player: Player) => {
    const newParticipants = [...selectedParticipants, player];
    setSelectedParticipants(newParticipants);
    setData(
      "participants",
      newParticipants.map(p => p.player_id)
    );
  };

  // 共同編集者の追加処理
  const handleCollaboratorAdd = (player: Player) => {
    const newCollaborators = [...selectedCollaborators, player];
    setSelectedCollaborators(newCollaborators);
    setData(
      "collaborators",
      newCollaborators.map(p => p.player_id)
    );
  };

  // 参加者の削除処理
  const removeParticipant = (playerPlayerId: string) => {
    const newParticipants = selectedParticipants.filter(p => p.player_id !== playerPlayerId);
    setSelectedParticipants(newParticipants);
    setData(
      "participants",
      newParticipants.map(p => p.player_id)
    );
  };

  // 共同編集者の削除処理
  const removeCollaborator = (playerPlayerId: string) => {
    const newCollaborators = selectedCollaborators.filter(p => p.player_id !== playerPlayerId);
    setSelectedCollaborators(newCollaborators);
    setData(
      "collaborators",
      newCollaborators.map(p => p.player_id)
    );
  };

  // 公開状態のオプション
  const publicStatusOptions = createListCollection({
    items: [
      { label: "公開", value: "published" },
      { label: "限定公開", value: "restricted" },
    ],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    put(event_path(event.id));
  };

  const handleCancel = () => {
    window.history.back();
  };

  return (
    <Container maxW="4xl" py={8}>
      <VStack gap={8} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="2xl" color="gray.800">
            イベントの編集
          </Heading>
          <Text color="gray.600" mt={2}>
            イベントの詳細を編集してください。
          </Text>
        </Box>

        {/* Main Form Card */}
        <Card.Root bg="white" shadow="lg" borderRadius="xl">
          <Card.Body p={8}>
            <Box as="form" onSubmit={handleSubmit}>
              <Stack gap={6}>
                {/* イベント名 */}
                <Field.Root invalid={!!errors.name}>
                  <Field.Label fontWeight="semibold" fontSize="sm">
                    イベント名 *
                  </Field.Label>
                  <Input
                    value={data.name}
                    onChange={e => setData("name", e.target.value)}
                    placeholder="イベント名を入力してください"
                    borderRadius="lg"
                    bg="gray.50"
                    _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                  />
                  <Field.ErrorText>{errors.name}</Field.ErrorText>
                </Field.Root>

                {/* 公開状態 */}
                <Field.Root invalid={!!errors.public_status}>
                  <Field.Label fontWeight="semibold" fontSize="sm">
                    公開状態
                  </Field.Label>
                  <Select.Root
                    collection={publicStatusOptions}
                    value={[data.public_status]}
                    onValueChange={value => setData("public_status", value.value[0])}
                    size="lg"
                  >
                    <Select.Control>
                      <Select.Trigger
                        borderRadius="lg"
                        bg="gray.50"
                        _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                      >
                        <Select.ValueText placeholder="公開状態を選択" />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                        <Select.ClearTrigger />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content borderRadius="lg" shadow="xl">
                        {publicStatusOptions.items.map(option => (
                          <Select.Item item={option} key={option.value}>
                            {option.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                  <Field.ErrorText>{errors.public_status}</Field.ErrorText>
                </Field.Root>

                {/* 開始時間 */}
                <Field.Root invalid={!!errors.start_at}>
                  <Field.Label fontWeight="semibold" fontSize="sm">
                    開始時間 *
                  </Field.Label>
                  <Input
                    type="datetime-local"
                    value={data.start_at}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData("start_at", e.target.value)}
                    borderRadius="lg"
                    bg="gray.50"
                    _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                  />
                  <Field.ErrorText>{errors.start_at}</Field.ErrorText>
                </Field.Root>

                {/* 終了時間 */}
                <Field.Root invalid={!!errors.end_at}>
                  <Field.Label fontWeight="semibold" fontSize="sm">
                    終了時間 *
                  </Field.Label>
                  <Input
                    type="datetime-local"
                    value={data.end_at}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData("end_at", e.target.value)}
                    borderRadius="lg"
                    bg="gray.50"
                    _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                  />
                  <Field.ErrorText>{errors.end_at}</Field.ErrorText>
                </Field.Root>

                <PrefectureSelect value={data.prefecture} onChange={value => setData("prefecture", value)} />

                {/* 参加者 */}
                <Box>
                  <Field.Root>
                    <Field.Label fontWeight="semibold" fontSize="sm">
                      参加者
                    </Field.Label>
                    <Field.HelperText color="gray.500" fontSize="xs" mb={2}>
                      参加者を検索して追加してください
                    </Field.HelperText>
                    <PlayerSearchInput
                      selectedPlayers={selectedParticipants}
                      onPlayerChange={handleParticipantAdd}
                      placeholder="参加者を検索してください"
                      minSearchLength={1}
                    />
                  </Field.Root>

                  {/* 選択済み参加者 */}
                  {selectedParticipants.length > 0 && (
                    <Box mt={2}>
                      <Flex wrap="wrap" gap={2}>
                        {selectedParticipants.map(player => (
                          <Badge key={player.player_id} p={2} borderRadius="lg" display="flex" alignItems="center" gap={2}>
                            <Box>
                              <Text fontWeight="bold" fontSize="sm">
                                {player.display_name}
                              </Text>
                              <Text fontSize="xs">{player.player_id}</Text>
                            </Box>
                            <CloseButton size="2xs" _hover={{ bg: "gray.200" }} onClick={() => removeParticipant(player.player_id)} ml={2} />
                          </Badge>
                        ))}
                      </Flex>
                    </Box>
                  )}
                </Box>

                {/* 編集者 */}
                <Box>
                  <Field.Root>
                    <Field.Label fontWeight="semibold" fontSize="sm">
                      共同編集者
                    </Field.Label>
                    <Field.HelperText color="gray.500" fontSize="xs" mb={2}>
                      共同編集者はイベントの詳細を変更できます
                    </Field.HelperText>
                    <PlayerSearchInput
                      selectedPlayers={selectedCollaborators}
                      onPlayerChange={handleCollaboratorAdd}
                      placeholder="編集者を検索して追加"
                      minSearchLength={2}
                    />
                  </Field.Root>

                  {/* 選択済み編集者 */}
                  {selectedCollaborators.length > 0 && (
                    <Box mt={2}>
                      <Flex wrap="wrap" gap={2}>
                        {selectedCollaborators.map(player => (
                          <Badge key={player.player_id} p={2} borderRadius="lg" display="flex" alignItems="center" gap={2}>
                            <Box>
                              <Text fontWeight="bold" fontSize="sm">
                                {player.display_name}
                              </Text>
                              <Text fontSize="xs">{player.player_id}</Text>
                            </Box>
                            <CloseButton size="2xs" _hover={{ bg: "gray.200" }} onClick={() => removeCollaborator(player.player_id)} ml={2} />
                          </Badge>
                        ))}
                      </Flex>
                    </Box>
                  )}
                </Box>

                {/* アクションボタン */}
                <Flex gap={4} mt={4}>
                  <Button
                    type="submit"
                    colorPalette="blue"
                    size="xl"
                    borderRadius="xl"
                    loading={processing}
                    loadingText="更新中..."
                    _hover={{
                      transform: "translateY(-2px)",
                      shadow: "xl",
                    }}
                    transition="all 0.2s"
                    flex={1}
                  >
                    {processing ? "更新中..." : "更新"}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    size="xl"
                    borderRadius="xl"
                    onClick={handleCancel}
                    _hover={{
                      transform: "translateY(-2px)",
                      shadow: "xl",
                    }}
                    transition="all 0.2s"
                    flex={1}
                  >
                    キャンセル
                  </Button>
                </Flex>
              </Stack>
            </Box>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Container>
  );
}
